import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, TimePickerDesignTokens } from '../types';
import { buildBoxShadow } from './common';
import type { CSSBlock } from '../types/common';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

export const getTimePickerTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
  transitionDuration: number,
  transitionEasing: string,
): TimePickerDesignTokens => {
  const { tokens, typography, spacing, borderRadius, halos } = theme;
  const { colors } = tokens;

  const labelMediumFont = {
    'font-family': typography.labelMedium.fontFamily,
    'font-size': typography.labelMedium.fontSize,
    'font-weight': `${typography.labelMedium.fontWeight}`,
    'line-height': typography.labelMedium.lineHeight,
  };

  const fieldTransition = `box-shadow ${transitionDuration}ms ${transitionEasing}, border-color ${transitionDuration}ms ${transitionEasing}`;

  const fieldBoxBase = {
    display: 'flex',
    'align-items': 'center',
    height: '34px',
    'box-sizing': 'border-box',
    'border-width': '1px',
    'border-style': 'solid',
    'border-color': colors.border.value,
    'border-radius': px(borderRadius.small),
    'padding-right': '7px',
    'padding-left': '11px',
    'user-select': 'none',
    ...labelMediumFont,
  } satisfies CSSBlock;

  const errorState = { 'border-color': colors.border.error.value };

  const disabledBox = {
    background: colors.background.neutral.inactive.value,
    'border-color': colors.border.inactive.value,
    color: colors.text.inactive.value,
    cursor: 'not-allowed',
  };

  const noHalo = { 'box-shadow': 'none' };

  const segmentSelection = {
    'background-color': colors.background.selected.value,
    color: colors.text.inverse.value,
    'border-radius': px(borderRadius.medium),
  };

  return {
    field: {
      _: {
        ...fieldBoxBase,
        color: colors.text.primary.value,
        transition: fieldTransition,
        '&:hover': {
          'box-shadow': halos.hover,
        },
        '&:focus-within': {
          'box-shadow': halos.focus,
          'border-color': colors.border.focused.value,
        },
        '&:active': {
          'box-shadow': halos.active,
        },
      },
      error: {
        true: errorState,
        false: {},
      },
      disabled: {
        true: {
          ...disabledBox,
          ...noHalo,
          '&:hover': noHalo,
          '&:focus-within': noHalo,
          '&:active': noHalo,
        },
        false: {},
      },
      open: {
        true: {
          'border-color': colors.border.focused.value,
          'box-shadow': halos.focus,
        },
        false: {},
      },
    },
    segment: {
      _: {
        'padding-top': '2.5px',
        'padding-bottom': '2.5px',
        'text-align': 'center',
        border: 'none',
        outline: 'none',
        width: '24px',
        'caret-color': 'transparent',
        background: 'transparent',
        color: colors.text.tertiary.value,
        ...labelMediumFont,
        '&:focus': segmentSelection,
        '&::selection': segmentSelection,
      },
      hasValue: {
        true: {
          color: colors.text.primary.value,
        },
        false: {
          color: colors.text.tertiary.value,
        },
      },
      disabled: {
        true: {
          background: colors.background.neutral.inactive.value,
          color: colors.text.inactive.value,
          cursor: 'not-allowed',
        },
        false: {},
      },
    },
    separator: {
      _: {
        width: '8px',
        height: '24px',
        display: 'flex',
        'justify-content': 'center',
        'align-content': 'center',
      },
    },
    icon: {
      _: {
        color: colors.icon.secondary.value,
        'margin-right': px(spacing.XXS),
      },
      disabled: {
        true: {
          color: colors.icon.inactive.value,
        },
        false: {},
      },
    },
    placeholder: {
      _: {
        ...fieldBoxBase,
        color: colors.text.inactive.value,
      },
      error: {
        true: errorState,
        false: {},
      },
      disabled: {
        true: disabledBox,
        false: {},
      },
    },
  };
};

export const getTimePickerTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): TimePickerDesignTokens => {
  const input = tokens?.component?.input;
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;

  const textPrimary = semantic?.['text-color']?.medium?.default;
  const textSubtle = semantic?.['text-color']?.light;
  const textPlaceholder = semantic?.['text-color']?.placeholder;
  const iconMedium = semantic?.['icon-color']?.medium;
  const bgSurface = semantic?.['background-color']?.surface?.[100];
  const bgSelected = (
    input?.['background-color'] as
      | { selected?: string; pressed?: string }
      | undefined
  )?.selected;
  const textSelected = input?.['text-color'] as
    | { selected?: string }
    | string
    | undefined;
  const segmentTextSelected =
    typeof textSelected === 'string' ? textSelected : textSelected?.selected;
  const borderBottomColors = input?.['border-bottom-color'] as
    | { hover?: string; focus?: string; pressed?: string }
    | undefined;
  const line = input?.line;
  const spacing8 = generic?.spacing?.['8'];
  const spacing24 = generic?.spacing?.['24'];
  const spacingNone = generic?.spacing?.none;
  const fieldBorderColor = input?.box?.['border-color'];
  const errorBorderColor = input?.error?.['border-bottom-color'];
  const focusedBorderColor = borderBottomColors?.focus;
  const borderRadius4 = generic?.['border-radius']?.['4'];
  const disabledOpacity = input?.opacity?.disabled;

  const fieldTransition =
    input?.['transition-duration'] !== undefined &&
    input?.['transition-timing-function'] !== undefined
      ? `box-shadow ${input['transition-duration']} ${input['transition-timing-function']}, border-color ${input['transition-duration']} ${input['transition-timing-function']}`
      : undefined;

  const labelFont = {
    'font-family': fontFamily,
    'font-size': input?.['font-size'],
    'font-weight': input?.['font-weight'],
    'line-height': input?.['line-height'],
  };

  const weaveFieldBase = {
    display: 'flex',
    'align-items': 'center',
    height: input?.['min-height'],
    'min-height': input?.['min-height'],
    color: textPrimary,
    'box-sizing': 'border-box',
    'background-color': bgSurface,
    'border-width': input?.box?.['border-width'],
    'border-style': input?.box?.['border-style'],
    'border-color': fieldBorderColor,
    'border-top-left-radius': input?.['border-top-left-radius'],
    'border-top-right-radius': input?.['border-top-right-radius'],
    'border-bottom-left-radius': input?.['border-bottom-left-radius'],
    'border-bottom-right-radius': input?.['border-bottom-right-radius'],
    'padding-top': input?.box?.['padding-top'],
    'padding-bottom': input?.box?.['padding-bottom'],
    'padding-left': input?.box?.['padding-left'],
    'padding-right': input?.box?.['padding-right'],
    cursor: 'pointer',
    'user-select': 'none',
    transition: fieldTransition,
    ...labelFont,
    '& [data-testid="Control__toggle"]': {
      height: input?.['line-height'],
      display: 'flex',
      'align-items': 'center',
    },
    '& [data-testid="Control__toggle"] svg': {
      color: iconMedium,
    },
    '&:hover': {
      'border-color': borderBottomColors?.hover,
      'box-shadow': buildBoxShadow(input, 'hover'),
    },
    '&:focus-within:not(:active)': {
      'border-color': focusedBorderColor,
      'box-shadow': buildBoxShadow(input, 'focus'),
    },
    '&:active': {
      'border-color': borderBottomColors?.pressed,
      'box-shadow': buildBoxShadow(input, 'pressed'),
    },
  } satisfies CSSBlock;

  const weaveFieldOpen = {
    'border-color': focusedBorderColor,
    'box-shadow': buildBoxShadow(input, 'focus'),
  } satisfies CSSBlock;

  const noHalo = { 'box-shadow': 'none' };

  const weaveFieldError = {
    'border-color': errorBorderColor,
    ...noHalo,
    '&:hover': {
      'border-color': errorBorderColor,
      ...noHalo,
    },
    '&:focus-within:not(:active)': {
      'border-color': errorBorderColor,
      ...noHalo,
    },
    '&:active': {
      'border-color': errorBorderColor,
      ...noHalo,
    },
  } satisfies CSSBlock;

  const segmentSelection = {
    'background-color': bgSelected,
    color: segmentTextSelected,
    'border-radius': borderRadius4,
  };

  return {
    field: {
      _: weaveFieldBase,
      error: {
        true: weaveFieldError,
        false: {},
      },
      disabled: {
        true: {
          cursor: 'not-allowed',
          opacity: disabledOpacity,
          '&:hover': noHalo,
          '&:focus-within': noHalo,
          '&:active': noHalo,
        },
        false: {},
      },
      open: {
        true: weaveFieldOpen,
        false: {},
      },
    },
    segment: {
      _: {
        'padding-top': spacingNone,
        'padding-bottom': spacingNone,
        'text-align': 'center',
        border: 'none',
        outline: 'none',
        width: spacing24,
        'caret-color': 'transparent',
        background: 'transparent',
        color: textSubtle,
        ...labelFont,
        '&:focus': segmentSelection,
        '&::selection': segmentSelection,
      },
      hasValue: {
        true: {
          color: textPrimary,
        },
        false: {
          color: textSubtle,
        },
      },
      disabled: {
        true: {},
        false: {},
      },
    },
    separator: {
      _: {
        width: spacing8,
        height: spacing24,
        display: 'flex',
        'justify-content': 'center',
        'align-content': 'center',
        color: textPrimary,
      },
    },
    icon: {
      _: {
        color: iconMedium,
        /**
         * Weave has no dedicated icon-gap token; the trailing gap after the
         * icon is spec'd as the line child-end-container `padding-left`. We map
         * that slot onto the icon's `margin-right` so the gap lands correctly.
         */
        'margin-right': line?.['child-end-container']?.['padding-left'],
      },
      disabled: {
        true: {},
        false: {},
      },
    },
    placeholder: {
      _: {
        ...weaveFieldBase,
        color: textPlaceholder,
        cursor: 'pointer',
      },
      error: {
        true: weaveFieldError,
        false: {},
      },
      disabled: {
        true: {
          cursor: 'not-allowed',
          opacity: disabledOpacity,
        },
        false: {},
      },
    },
  };
};
